import { persons as kycAcceptance } from '@repo/api-kit/kyc-acceptance';
import type { KycResponse } from '@repo/api-kit/queries/due-diligence/kyc';

const {
  ApiKycCurrentStatus,
  ApiProcessStatus,
  ApiProvider,
  ApiIdnowProviderDetailsStatus,
  ApiIdnowProviderDetailsErrorReason,
  ApiProcessType,
} = kycAcceptance.firstparty.v1;

export const MOCKED_TOKEN = 'mock-idnow-token-123';
export const MOCKED_VERIFICATION_URL = 'https://idnow.example.com/verify';

interface CreateKycResponseWithIdnowOptions {
  providerStatus?: (typeof ApiIdnowProviderDetailsStatus)[keyof typeof ApiIdnowProviderDetailsStatus];
  reason?: (typeof ApiIdnowProviderDetailsErrorReason)[keyof typeof ApiIdnowProviderDetailsErrorReason];
  kycCurrentStatus?: KycResponse['kycCurrentStatus'];
  activeProcess?: null;
  processStatus?: (typeof ApiProcessStatus)[keyof typeof ApiProcessStatus];
  providerDetails?: null;
}

/**
 * Creates a mock KYC response with IDNow provider details
 */
export function createKycResponseWithIdnow(
  options: Readonly<CreateKycResponseWithIdnowOptions> = {}
): KycResponse {
  const {
    providerStatus = ApiIdnowProviderDetailsStatus.pending,
    reason = ApiIdnowProviderDetailsErrorReason.ERROR_REASON_UNSPECIFIED,
    kycCurrentStatus = ApiKycCurrentStatus.pending,
    processStatus = ApiProcessStatus.pending_liveness,
  } = options;

  const providerDetails =
    options.providerDetails === null
      ? null
      : {
          $case: 'idnowProviderDetails' as const,
          value: {
            verificationUrl: MOCKED_VERIFICATION_URL,
            token: MOCKED_TOKEN,
            status: providerStatus,
            reason,
          },
        };

  const activeProcess =
    options.activeProcess === null
      ? null
      : {
          type: ApiProcessType.initial,
          processStatus,
          provider: ApiProvider.idnow,
          providerDetails,
          signatureRequired: false,
        };

  return {
    kycCurrentStatus,
    activeProcess,
  };
}

export function createPendingIdnowKycResponse() {
  return createKycResponseWithIdnow();
}

export function createOnCallIdnowKycResponse() {
  return createKycResponseWithIdnow({
    providerStatus: ApiIdnowProviderDetailsStatus.on_call,
    kycCurrentStatus: ApiKycCurrentStatus.pending,
  });
}

export function createWaitingResultsIdnowKycResponse() {
  return createKycResponseWithIdnow({
    providerStatus: ApiIdnowProviderDetailsStatus.waiting_results,
    kycCurrentStatus: ApiKycCurrentStatus.pending,
  });
}

export function createRefusedIdnowKycResponse() {
  return createKycResponseWithIdnow({
    providerStatus: ApiIdnowProviderDetailsStatus.pending,
    kycCurrentStatus: ApiKycCurrentStatus.refused,
  });
}

export function createCanceledIdnowKycResponse() {
  return createKycResponseWithIdnow({
    providerStatus: ApiIdnowProviderDetailsStatus.canceled,
    reason: ApiIdnowProviderDetailsErrorReason.user_abort,
    kycCurrentStatus: ApiKycCurrentStatus.pending,
  });
}

export function createFailedIdnowKycResponse() {
  return createKycResponseWithIdnow({
    providerStatus: ApiIdnowProviderDetailsStatus.failed,
    reason: ApiIdnowProviderDetailsErrorReason.connection_issues,
    kycCurrentStatus: ApiKycCurrentStatus.pending,
  });
}

export function createSubmittedIdnowKycResponse() {
  return createKycResponseWithIdnow({
    processStatus: ApiProcessStatus.submitted,
    providerDetails: null,
  });
}

/**
 * Edge case: Token is empty string
 * This should never happen but tests error handling
 */
export function createEmptyTokenIdnowResponse(): KycResponse {
  return {
    kycCurrentStatus: ApiKycCurrentStatus.pending,
    activeProcess: {
      type: ApiProcessType.initial,
      processStatus: ApiProcessStatus.pending_liveness,
      provider: ApiProvider.idnow,
      providerDetails: {
        $case: 'idnowProviderDetails',
        value: {
          verificationUrl: '',
          token: '',
          status: ApiIdnowProviderDetailsStatus.pending,
          reason: ApiIdnowProviderDetailsErrorReason.ERROR_REASON_UNSPECIFIED,
        },
      },
      signatureRequired: false,
    },
  };
}
